import { createInstance, InitOptions } from 'i18next';
import HttpApi, { HttpBackendOptions } from 'i18next-http-backend';

import { CDN_DEV_URL } from '@zooplus/commons-lang';
import { CDN_PROD_URL } from '@zooplus/commons-lang/dist/es/services/envService';

import { ENV } from '../environment';
import { PACKAGE_TRANSLATIONS_NAMESPACE_PREFIX } from '../hooks/use-translations';

import { logError } from './error-service';

// Replace single curly braces interpolation {var} to double curly braces {{var}}
// This is useful if you have translations from oracle database that were not replaced before.
// Interpolations in translations extracted with "frontend-scripts extract-translations -- new" are already replaced
const replaceInterpolations = (localizations: Record<string, string>) => {
  return Object.fromEntries(
    Object.entries(localizations).map(([key, value]) => [key, replaceInterpolation(value)]),
  );
};

const replaceInterpolation = (value: string) => {
  return value.replace(/(\{[a-zA-Z0-9_.]+\})/gi, '{$1}');
};

export const initializeI18n = (
  locale: string,
  localizations?: Record<string, string>, // Remove this parameter if you don't have initialization translations
) => {
  const config: InitOptions = {
    lng: locale,
    // Default namespace is "translation", let's put initialization translations in this namespace (these translations might come from a BFF during SSR for example)
    // Remove this parameter if you don't have initialization translations
    resources: localizations && { [locale]: { translation: replaceInterpolations(localizations) } },
    initImmediate: true, // Avoid using async loading of translations ... this would be incompatible with SSR
    react: { useSuspense: false }, // React-i18next counterpart for initImmediate=true
    load: 'currentOnly', // Only load translation in given locale, do not try any fallback if not found
    parseMissingKeyHandler: () => '', // Uncomment if you want translations related to not found keys to be displayed as empty
    // debug: true, // Uncomment to see missing translations in logs
    // FYI : If you use SSR this won't work. Check you are running the code in browser before setting this backend property.
    backend: {
      loadPath: (locales: string[], ns: string[]) => {
        // Disable http backend for default translation namespace
        if (ns[0] === 'translation' || locales[0] === 'dev') {
          return false;
        }
        // Serve translations from devserver public folder for this project's translations
        if (ENV.isLocalhost && ns[0].startsWith(PACKAGE_TRANSLATIONS_NAMESPACE_PREFIX)) {
          return `${CDN_DEV_URL}/media/${ns[0]}/${locales[0]}.json`;
        }
        if (!ENV.isLocalhost && !ENV.isDev) {
          return `${CDN_PROD_URL}/media/${ns[0]}/${locales[0]}.json`;
        }
        // Serve translations from Zooplus CDN for other packages
        // FYI : We only use this service for local development. So we rely on dev CDN.
        return `${CDN_DEV_URL}/media/${ns[0]}/${locales[0]}.json`;
      },
      reloadInterval: 3 * 60 * 60 * 1000, //reload every 3 hours
    } as HttpBackendOptions,
    partialBundledLanguages: true, // Allow to load translations from different places for same language,
    interpolation: {
      escapeValue: false,
    },
  };

  const instance = createInstance(config);

  // FYI : If you use SSR this won't work. Surround it with if(typeof window !== 'undefined'){...}
  instance.use(HttpApi);
  instance.on('failedLoading', (locale: string, ns: string, message: string) => {
    logError({
      error: new Error(message),
      action: `Could not load ${locale} in ns ${ns}, error message: ${message}`,
    });
  });

  instance.init();
  return instance;
};
